import React, { useEffect, useState } from 'react';
import Title from './Title';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  Avatar,
  TextField,
  Tooltip,
} from '@mui/material';
import { onSnapshot, collection, query, where, arrayRemove, doc, updateDoc, getDoc, orderBy } from '@firebase/firestore';
import { db } from '../service/Firebase-config';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import CircleIcon from '@mui/icons-material/Circle';
import { stringAvatar, chunkArray } from '../common/util';
import TablePaginationActions from '../common/TablePagination';
import DialogAssign from '../form/DialogAssign';
import { useGlobalContext } from '../context/GlobalContext';

export default function StudentTable() {
  const { selectedUser, setSelectedUser } = useGlobalContext();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [data, setData] = useState([]);
  const [students, setStudents] = useState([]);
  const [openAssign, setOpenAssign] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(false);

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - students.length) : 0;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleQuery = (event) => {
    const query = event.target.value.toLowerCase();
    const filteredData = students.filter(row => row.displayName.toLowerCase().includes(query));
    setData(filteredData);
  };

  const handleUnAssigned = async (row) => {
    const selectedRowRef = doc(db, `students`, row.uid);
    await updateDoc(selectedRowRef, { teacherList: arrayRemove(selectedUser.uid) });

    const selectedTeacherRef = doc(db, `teachers`, selectedUser.uid);
    await updateDoc(selectedTeacherRef, { studentList: selectedUser.studentList.filter(std => std !== row.id) }).then(async () => {
      const updatedInfo = await getDoc(doc(db, `teachers`, selectedUser.uid));
      setSelectedUser(updatedInfo.data());
    }).then(() => {
      setOpenConfirm(false);
    });
    setSelectedRow(null);
  };

  useEffect(() => {
    if (selectedUser && selectedUser.studentList?.length > 0) {
      const collectionRef = collection(db, "students");
  
      const chunks = chunkArray(selectedUser.studentList, 30);
      let unsubscribes = [];
  
      let studentMap = new Map();
  
      chunks.forEach((chunk) => {
        const whereQuery = query(
          collectionRef,
          where("uid", "in", chunk),
          orderBy("displayName")
        );
  
        const unsubscribe = onSnapshot(whereQuery, (snapshot) => {
          snapshot.docs.forEach((doc) => {
            studentMap.set(doc.id, { ...doc.data(), id: doc.id });
          });
  
          const uniqueStudents = Array.from(studentMap.values()).sort((a, b) =>
            a.displayName.localeCompare(b.displayName)
          );
  
          setStudents(uniqueStudents);
          setData(uniqueStudents);
        });
  
        unsubscribes.push(unsubscribe);
      });
  
      return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
    } else {
      setStudents([]);
      setData([]);
    }
  }, [selectedUser]);
  
  const renderAvatar = (row) => (
    row.photoUrl ? <Avatar src={row.photoUrl} /> : <Avatar {...stringAvatar(row.displayName)} />
  );

  return (
    <React.Fragment>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Title>Student List</Title>
          <TextField
            id="input-with-sx"
            label="Search Name"
            size="small"
            variant="outlined"
            onChange={handleQuery}
            sx={{ marginLeft: 2 }}
          />
        </Box>
        <Button variant="outlined" onClick={() => setOpenAssign(true)}>Assign</Button>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Stars</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Grade</TableCell>
            <TableCell>School</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(rowsPerPage > 0
            ? data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            : data
          ).map((row) => (
            <TableRow key={row.id}>
              <TableCell style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {renderAvatar(row)}
                <Box display="flex" alignItems="center">
                  {row.displayName}
                  <Box ml={1}>
                    <Tooltip title={row.active ? 'Active' : 'Inactive'}>
                      <CircleIcon sx={{ color: row.active ? '#5Dbb63' : '#FF0000', fontSize: 15 }} />
                    </Tooltip>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>{row.totalStars}</TableCell>
              <TableCell>{row.email}</TableCell>
              <TableCell>{row.grade}</TableCell>
              <TableCell>{row.school}</TableCell>
              <TableCell>
                <IconButton variant="contained" onClick={() => { setSelectedRow(row); setOpenConfirm(true); }}>
                  <PersonRemoveIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {emptyRows > 0 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <TableCell colSpan={6} />
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
              colSpan={6}
              count={students.length}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: {
                  'aria-label': 'rows per page',
                },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
      <DialogAssign open={openAssign} handleClose={() => setOpenAssign(false)} />
      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)} >
        <DialogTitle>Are you sure you want to unassign {selectedRow?.displayName}?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>
          <Button onClick={() => handleUnAssigned(selectedRow)}>Unassign</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
